import type { ItemDefinition } from './definitions';

/**
 * Whether one owned item may be traded, and why not when it may not.
 *
 * ONE resolver, called by the vault, by the trade and by the interface, so
 * "can this be offered" is a single fact instead of three checks that drift
 * apart at a boundary. The trade re-derives it inside its own transaction from
 * rows it has locked; the wire copy is a display of that answer, never the
 * authority for it.
 *
 * ## Two independent gates
 *
 * A definition says whether an item of that KIND may ever be traded. An
 * instance says whether THIS copy may be traded right now. Both have to pass,
 * and they fail for different reasons:
 *
 * - `not_tradable` — the catalog forbids it for everybody, always.
 * - `bound` — this particular copy is bound to the account that received it.
 * - `equipped` / `active_pet` — it is in use.
 * - `reserved` — another trade is already holding it.
 * - `unavailable` — consumed, retired, or no longer owned.
 *
 * ## Why binding is per INSTANCE
 *
 * The free starter ship and cannon must never be sellable, but `ship1` itself
 * is an ordinary ship: a second copy from a drop, a gacha pull or a purchase is
 * perfectly tradable. Binding the DEFINITION would make every future copy of
 * the same model worthless, so the flag lives on the row that was granted, set
 * from the grant's own provenance.
 */

/** Why an item cannot be traded. Null when it can. */
export const TRADE_BLOCK_REASONS = [
  /** The catalog forbids trading this kind of item at all. */
  'not_tradable',
  /** This copy is bound to its account: the free starter kit, and grants like it. */
  'bound',
  /** Occupying a loadout slot. Unequip it first. */
  'equipped',
  /** The active pet. Deactivate it first. */
  'active_pet',
  /** Another trade is holding it. */
  'reserved',
  /** Consumed, retired, or not owned by this account. */
  'unavailable',
] as const;
export type TradeBlockReason = (typeof TRADE_BLOCK_REASONS)[number];

/** What a caller has to know about one copy to answer the question. */
export interface TradabilitySubject {
  readonly definition: ItemDefinition | null;
  /** `'owned'`; anything else is history rather than inventory. */
  readonly state: string;
  /** Bound to this account by the provenance of its grant. */
  readonly soulbound: boolean;
  /** Occupying a loadout slot right now. */
  readonly equipped: boolean;
  /** The account's active pet. Distinguished from `equipped` for the message. */
  readonly activePet: boolean;
  /** An active reservation is holding it. */
  readonly reserved: boolean;
}

export interface TradabilityVerdict {
  readonly tradable: boolean;
  readonly reason: TradeBlockReason | null;
}

const OK: TradabilityVerdict = Object.freeze({ tradable: true, reason: null });

function blocked(reason: TradeBlockReason): TradabilityVerdict {
  return Object.freeze({ tradable: false, reason });
}

/**
 * The answer, in a fixed order of precedence.
 *
 * The order matters for the MESSAGE, not the outcome: an equipped starter ship
 * is refused either way, and telling somebody to unequip it would send them to
 * do something that will not help. So permanent reasons are reported before
 * temporary ones, and the player is only ever asked to do something that works.
 */
export function resolveTradability(subject: TradabilitySubject): TradabilityVerdict {
  if (!subject.definition?.enabled || subject.state !== 'owned') {
    return blocked('unavailable');
  }
  if (!subject.definition.tradable) {
    return blocked('not_tradable');
  }
  if (subject.soulbound) {
    return blocked('bound');
  }
  // Reservation before equipment: a held copy is committed to a trade, and
  // unequipping it would not free it.
  if (subject.reserved) {
    return blocked('reserved');
  }
  if (subject.activePet) {
    return blocked('active_pet');
  }
  if (subject.equipped) {
    return blocked('equipped');
  }
  return OK;
}

/**
 * What to tell the player, in Brazilian Portuguese.
 *
 * Every temporary reason names the action that fixes it. A permanent one says
 * so plainly rather than implying there is something to try.
 */
export const TRADE_BLOCK_MESSAGES: Readonly<Record<TradeBlockReason, string>> = Object.freeze({
  not_tradable: 'Este item não pode ser negociado.',
  bound: 'Vinculado à conta. O equipamento inicial gratuito não pode ser negociado.',
  equipped: 'Desequipe este item antes de negociar.',
  active_pet: 'Desative este pet antes de negociar.',
  reserved: 'Reservado em outro trade.',
  unavailable: 'Indisponível.',
});

/** The short badge an inventory card shows. */
export const TRADE_STATUS_LABELS: Readonly<Record<TradeBlockReason | 'tradable', string>> =
  Object.freeze({
    tradable: 'NEGOCIÁVEL',
    not_tradable: 'NÃO NEGOCIÁVEL',
    bound: 'VINCULADO À CONTA',
    equipped: 'EQUIPADO',
    active_pet: 'ATIVO',
    reserved: 'RESERVADO EM TRADE',
    unavailable: 'INDISPONÍVEL',
  });

/**
 * Which grant provenances produce a BOUND copy.
 *
 * Stated as data rather than as an `if` in the grant path, so "the free starter
 * kit is bound" is one fact that the service, the migration's backfill and the
 * tests all read. Everything absent from this list is tradable-by-provenance:
 * a drop, a gacha pull, a merge and a purchase all produce ordinary copies.
 *
 * A future promotional or quest grant that must be bound is added HERE, and
 * inherits the whole rule with it.
 */
export const SOULBOUND_ORIGINS: readonly string[] = Object.freeze([
  'starter_kit',
  /**
   * Bought with Cash (ADR 0036). Nothing grants this origin yet.
   *
   * It is here BEFORE the purchase exists, deliberately. The rule it encodes is
   * a one-way door in the economy: a skin paid for with real money must never
   * become Gold. Left out, the default in ADR 0011 applies - a `purchase` copy
   * is "an ordinary, sellable item" - and the first Cash skin would be
   * listable on the Gold marketplace the day it shipped, opening a Cash → Gold
   * path the economy has never authorised.
   *
   * Adding it now costs nothing and cannot change any existing item: no row
   * carries this origin, because no code writes it. The browser cannot choose
   * an origin either - a grant's provenance is written by the server that
   * performs the grant.
   */
  'purchase_cash',
  /**
   * The free foundation skin, claimed once (ADR 0037). **Legacy.**
   *
   * The claim that wrote this origin was retired in ADR 0039 and nothing
   * produces it any more. It stays on this list because rows carrying it still
   * exist: an account that claimed one while the claim existed still owns it,
   * and dropping the string would make `resolveTradability` stop answering
   * `bound` for exactly those rows — turning something that was GIVEN AWAY into
   * a tradable item, which is the one thing retiring a giveaway must not do.
   *
   * Bound for the opposite reason to `purchase_cash`, and worth keeping stated:
   * that one is bound so real money cannot become Gold, and this one is bound
   * so a giveaway cannot. A free cosmetic that could be sold is a Gold faucet
   * with a button on it, and one that could be traded is an account's worth of
   * them farmed on throwaway sign-ups.
   */
  'foundation_grant',
  /**
   * A skin rolled out of the Cash gacha (ADR 0040).
   *
   * Bound for the same reason as `purchase_cash` and stated separately because
   * the two are different events: that one is a direct purchase, this one is a
   * draw that a purchase paid for. Both are a one-way door — Cash must never
   * become Gold — and a skin instance also carries an immutable socket capacity
   * that would otherwise become a tradable roll.
   */
  'skin_cash_gacha',
]);

export function isSoulboundOrigin(origin: string): boolean {
  return SOULBOUND_ORIGINS.includes(origin);
}
